///
/// EarthBrowser
///
/// Created by Matt Giger on 10/24/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Ground disc for the city weather vignette (vignette_plinth.glsl): a
/// unit disc in the local XZ plane that the node scales to the plinth
/// radius. Tinted by temperature, rimmed, shadowed under the cloud.
///

import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type color, rgba } from "@stovokor/math/color"
import { cube } from "@stovokor/math/cube"
import { type matrix } from "@stovokor/math/matrix"
import { sphere } from "@stovokor/math/sphere"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

const cSegments = 64

export class VignettePlinthNode extends NodeBase {
	private readonly plinthRenderer: PlinthRenderer
	private readonly geometry: DiscGeometry

	constructor(context: StovokorContext) {
		super()
		this.geometry = new DiscGeometry(context)
		this.plinthRenderer = new PlinthRenderer(context, this.geometry)
		this.renderer = this.plinthRenderer
	}

	public set tint(value: color) {
		this.plinthRenderer.color.value = value
	}

	public set coverage(value: number) {
		this.plinthRenderer.coverage.value = value
	}

	public set night(value: number) {
		this.plinthRenderer.night.value = value
	}

	public set opacity(value: number) {
		this.plinthRenderer.opacity.value = value
	}
}

/** Unit disc fan in the XZ plane */
class DiscGeometry implements Geometry {
	public mode: DrawMode = DrawMode.Triangles
	public position: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public bounds = new cube(-1, 1)
	public boundingSphere = new sphere(1)
	public available: boolean = true

	constructor(context: StovokorContext) {
		this.position = new VertexAttribute(context, 3)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position])

		this.position.addPos(0, 0, 0)
		for (let i = 0; i <= cSegments; i++) {
			const a = (i / cSegments) * Math.PI * 2
			this.position.addPos(Math.cos(a), 0, Math.sin(a))
		}
		for (let i = 1; i <= cSegments; i++) {
			this.indexBuffer.triangle(0, i + 1, i)
		}
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}

class PlinthRenderer extends Renderer {
	public modelViewProjection = new UniformDynamic<matrix>()
	public color = new UniformDynamic<color>(rgba(0.7, 0.85, 1, 0.9))
	public coverage = new UniformDynamic<number>(0)
	public night = new UniformDynamic<number>(0)
	public opacity = new UniformDynamic<number>(0)

	constructor(context: StovokorContext, geometry: Geometry) {
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("vignette_plinth"), geometry, [transparentMaterial])
		// Visible from either side (tilted views can look up at it over a limb)
		this.drawState.cullFace = false
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
	}
}
